import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { SafeHtml } from "@/components/SafeHtml";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Heading3, Link2, Unlink, Undo, Redo, Eye, FileCode,
  Sparkles, Save, Loader2, Calendar, Clock, RefreshCw, Lightbulb,
  Image, Tag, X, ArrowLeft, ChevronRight, FolderOpen, Upload
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger
} from "@/components/ui/popover";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// MenuBar komponent
const MenuBar = ({ editor }) => {
  const [linkUrl, setLinkUrl] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);

  if (!editor) return null;

  const setLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
    }
    setLinkUrl("");
    setLinkOpen(false);
  };

  const buttonClass = (active) =>
    `h-8 w-8 p-0 ${active ? "bg-blue-100 text-blue-700" : "hover:bg-slate-100"}`;

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-slate-50 rounded-t-lg">
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive("bold"))} title="Pogrubienie">
        <Bold className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive("italic"))} title="Kursywa">
        <Italic className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={buttonClass(editor.isActive("underline"))} title="Podkreslenie">
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={buttonClass(editor.isActive("heading", { level: 2 }))} title="Naglowek H2">
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={buttonClass(editor.isActive("heading", { level: 3 }))} title="Naglowek H3">
        <Heading3 className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive("bulletList"))} title="Lista punktowana">
        <List className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive("orderedList"))} title="Lista numerowana">
        <ListOrdered className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 mx-1" />
      
      <Popover open={linkOpen} onOpenChange={setLinkOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className={buttonClass(editor.isActive("link"))} title="Dodaj link">
            <Link2 className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3">
          <div className="space-y-2">
            <p className="text-xs font-medium">Dodaj link</p>
            <Input placeholder="https://example.com" value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)} className="h-8 text-xs"
              onKeyDown={(e) => e.key === "Enter" && setLink()} />
            <Button size="sm" className="w-full h-8 text-xs" onClick={setLink}>Wstaw link</Button>
          </div>
        </PopoverContent>
      </Popover>
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")} className={buttonClass(false)} title="Usun link">
        <Unlink className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()} className={buttonClass(false)} title="Cofnij">
        <Undo className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()} className={buttonClass(false)} title="Ponow">
        <Redo className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default function BlogEditor({ post, onSave, onCancel }) {
  // Formularz
  const [title, setTitle] = useState(post?.title || "");
  const [content, setContent] = useState(post?.content || "");
  const [htmlSource, setHtmlSource] = useState(post?.content || "");
  const [featuredImage, setFeaturedImage] = useState(post?.featured_image || "");
  const [metaTitle, setMetaTitle] = useState(post?.meta_title || "");
  const [metaDescription, setMetaDescription] = useState(post?.meta_description || "");
  const [tags, setTags] = useState(post?.tags || "");
  const [excerpt, setExcerpt] = useState(post?.excerpt || "");
  const [status, setStatus] = useState(post?.status || "draft");
  const [scheduledAt, setScheduledAt] = useState(post?.scheduled_at || "");
  const [autoScheduleDays, setAutoScheduleDays] = useState(post?.auto_schedule_days || null);
  const [autoScheduleHour, setAutoScheduleHour] = useState(post?.auto_schedule_hour || 8);
  
  // Stany UI
  const [activeTab, setActiveTab] = useState("editor");
  const [saving, setSaving] = useState(false);
  const [generatingTopics, setGeneratingTopics] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [suggestedTopics, setSuggestedTopics] = useState([]);
  const [showTopics, setShowTopics] = useState(false);
  
  // Media picker
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [mediaImages, setMediaImages] = useState([]);
  const [mediaCategories, setMediaCategories] = useState([]);
  const [selectedMediaCategory, setSelectedMediaCategory] = useState("");
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { 
          class: "text-blue-600 underline hover:text-blue-800",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Underline,
      Placeholder.configure({ placeholder: "Zacznij pisac artykul lub uzyj AI..." }),
    ],
    content: post?.content || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose max-w-none focus:outline-none min-h-[400px] p-4",
      },
    },
    onUpdate: ({ editor }) => {
      setHtmlSource(editor.getHTML());
      setContent(editor.getHTML());
    },
  });

  // Sync content when post changes
  useEffect(() => {
    if (editor && post?.content !== editor.getHTML()) {
      editor.commands.setContent(post?.content || "");
      setHtmlSource(post?.content || "");
    }
  }, [post, editor]);

  // Apply HTML changes from source tab
  const applyHtmlChanges = () => {
    if (editor) editor.commands.setContent(htmlSource);
    setContent(htmlSource);
  };

  // Pobierz media z bazy
  const fetchMediaImages = async (category = "") => {
    setLoadingMedia(true);
    try {
      const url = category ? `${API}/media?category=${encodeURIComponent(category)}` : `${API}/media`;
      const res = await axios.get(url);
      setMediaImages(res.data || []);
    } catch (err) {
      console.error(err);
      setMediaImages([]);
    } finally {
      setLoadingMedia(false);
    }
  };

  // Pobierz kategorie mediów
  const fetchMediaCategories = async () => {
    try {
      const res = await axios.get(`${API}/media/categories`);
      setMediaCategories(res.data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Otwórz picker obrazków
  const openImagePicker = () => {
    setShowImagePicker(true);
    fetchMediaCategories();
    fetchMediaImages(selectedMediaCategory);
  };

  // Wybierz kategorię
  const handleCategoryChange = (cat) => {
    setSelectedMediaCategory(cat);
    fetchMediaImages(cat);
  };

  // Wybierz obrazek z biblioteki
  const selectMediaImage = (img) => {
    // Konstruuj pełny URL
    const imgUrl = img.url?.startsWith("http") ? img.url : `https://epidemos.pl${img.url}`;
    setFeaturedImage(imgUrl);
    setShowImagePicker(false);
    toast.success("Obrazek wybrany");
  };

  // Upload obrazka z komputera
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Walidacja
    if (!file.type.startsWith("image/")) {
      toast.error("Wybierz plik obrazka");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Maksymalny rozmiar pliku to 10MB");
      return;
    }
    
    setUploadingImage(true);
    try {
      // Konwertuj na base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const dataUrl = event.target.result;
        
        // Upload do API
        const res = await axios.post(`${API}/media/bulk-upload`, {
          files: [{ filename: file.name, data_url: dataUrl }],
          category: selectedMediaCategory || "Blog"
        });
        
        if (res.data.uploaded > 0 && res.data.items?.length > 0) {
          const uploaded = res.data.items[0];
          const imgUrl = uploaded.url?.startsWith("http") ? uploaded.url : `https://epidemos.pl${uploaded.url}`;
          setFeaturedImage(imgUrl);
          setShowImagePicker(false);
          toast.success("Obrazek wgrany i wybrany");
        } else {
          toast.error("Błąd uploadu");
        }
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error("Błąd uploadu: " + (err.response?.data?.detail || err.message));
      setUploadingImage(false);
    }
  };

  // Wygeneruj propozycje tematow AI
  const handleSuggestTopics = async () => {
    setGeneratingTopics(true);
    try {
      const res = await axios.post(`${API}/admin/blog/suggest-topics`, { ai_model: "gemini" });
      setSuggestedTopics(res.data.topics || []);
      setShowTopics(true);
    } catch (err) {
      toast.error("Blad generowania tematow");
      console.error(err);
    } finally {
      setGeneratingTopics(false);
    }
  };

  // Wygeneruj artykul AI na podstawie tematu
  const handleGenerateArticle = async (topic) => {
    setGeneratingContent(true);
    setShowTopics(false);
    try {
      const res = await axios.post(`${API}/admin/blog/generate`, { topic, ai_model: "gemini" });
      const data = res.data;
      
      setTitle(data.title || topic);
      setMetaTitle(data.meta_title || data.title?.substring(0, 60) || "");
      setMetaDescription(data.meta_description || "");
      setTags(data.tags || "");
      setExcerpt(data.excerpt || "");
      
      if (editor && data.content) {
        editor.commands.setContent(data.content);
        setHtmlSource(data.content);
        setContent(data.content);
      }
      
      toast.success(`Wygenerowano artykul (${data.word_count || 0} slow)`);
    } catch (err) {
      toast.error("Blad generowania artykulu: " + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setGeneratingContent(false);
    }
  };

  // Zapisz post
  const handleSave = async (publishNow = false) => {
    if (!title.trim()) {
      toast.error("Tytul jest wymagany");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        title,
        content: content || htmlSource,
        excerpt: excerpt || (content ? content.replace(/<[^>]+>/g, '').substring(0, 200) + "..." : ""),
        featured_image: featuredImage,
        meta_title: metaTitle || title.substring(0, 60),
        meta_description: metaDescription,
        tags,
        status: publishNow ? "published" : status,
        scheduled_at: status === "scheduled" ? scheduledAt : null,
        auto_schedule_days: autoScheduleDays,
        auto_schedule_hour: autoScheduleHour
      };
      
      if (post?.id) {
        await axios.put(`${API}/admin/blog/${post.id}`, payload);
        toast.success("Post zaktualizowany");
      } else {
        await axios.post(`${API}/admin/blog`, payload);
        toast.success(publishNow ? "Post opublikowany" : "Post zapisany");
      }
      
      onSave?.();
    } catch (err) {
      toast.error("Blad zapisu: " + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  // Statystyki
  const textContent = htmlSource.replace(/<[^>]+>/g, "");
  const charCount = textContent.length;
  const wordCount = textContent.split(/\s+/).filter(w => w).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Wstecz
          </Button>
          <h2 className="text-lg font-semibold">{post?.id ? "Edycja posta" : "Nowy post bloga"}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handleSave(false)} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Zapisz szkic
          </Button>
          <Button size="sm" onClick={() => handleSave(true)} disabled={saving || !title}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Publikuj
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Lewa kolumna - Edytor */}
        <div className="lg:col-span-2 space-y-4">
          {/* Tytul */}
          <Input
            placeholder="Tytul artykulu..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-xl font-semibold h-12"
          />

          {/* AI Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSuggestTopics}
              disabled={generatingTopics || generatingContent}
              className="gap-1"
            >
              {generatingTopics ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />}
              Wygeneruj tematy AI
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={() => title && handleGenerateArticle(title)}
              disabled={generatingContent || !title.trim()}
              className="gap-1"
            >
              {generatingContent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              Napisz AI
            </Button>
          </div>

          {/* Propozycje tematow */}
          {showTopics && suggestedTopics.length > 0 && (
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader className="py-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-blue-600" />
                    Propozycje tematow SEO
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowTopics(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="py-2">
                <div className="space-y-1">
                  {suggestedTopics.map((topic, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setTitle(topic);
                        handleGenerateArticle(topic);
                      }}
                      className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-between group"
                    >
                      <span className="line-clamp-1">{topic}</span>
                      <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity text-blue-600" />
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generowanie w toku */}
          {generatingContent && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="py-4 flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                <span className="text-sm text-amber-700">AI generuje artykul SEO... (moze potrwac do 2 minut)</span>
              </CardContent>
            </Card>
          )}

          {/* Edytor Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="border rounded-lg">
            <TabsList className="w-full justify-start rounded-t-lg rounded-b-none border-b bg-slate-50">
              <TabsTrigger value="editor" className="gap-1.5">
                <Bold className="w-3.5 h-3.5" /> Edytor
              </TabsTrigger>
              <TabsTrigger value="html" className="gap-1.5">
                <FileCode className="w-3.5 h-3.5" /> HTML
              </TabsTrigger>
              <TabsTrigger value="preview" className="gap-1.5">
                <Eye className="w-3.5 h-3.5" /> Podglad
              </TabsTrigger>
            </TabsList>

            <TabsContent value="editor" className="m-0">
              <MenuBar editor={editor} />
              <div className="min-h-[400px] max-h-[600px] overflow-y-auto">
                <EditorContent editor={editor} />
              </div>
            </TabsContent>

            <TabsContent value="html" className="m-0 p-4">
              <Textarea
                value={htmlSource}
                onChange={(e) => setHtmlSource(e.target.value)}
                className="font-mono text-xs min-h-[400px] resize-y"
                placeholder="<p>Tresc HTML...</p>"
              />
              <Button size="sm" onClick={applyHtmlChanges} className="mt-2">
                Zastosuj zmiany
              </Button>
            </TabsContent>

            <TabsContent value="preview" className="m-0 p-4">
              <SafeHtml
                className="prose prose-sm max-w-none min-h-[400px]"
                html={htmlSource}
              />
            </TabsContent>
          </Tabs>

          {/* Statystyki */}
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span>{charCount.toLocaleString()} znakow</span>
            <span>{wordCount.toLocaleString()} slow</span>
          </div>
        </div>

        {/* Prawa kolumna - Ustawienia */}
        <div className="space-y-4">
          {/* Obrazek wyrozniajacy */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Image className="w-4 h-4" /> Obrazek wyrozniajacy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {featuredImage && (
                <div className="relative rounded-lg overflow-hidden aspect-video bg-slate-100">
                  <img src={featuredImage} alt="" className="w-full h-full object-cover" />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 h-6 w-6 p-0"
                    onClick={() => setFeaturedImage("")}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
              
              {/* Przyciski wyboru */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openImagePicker}
                  className="flex-1 gap-1.5"
                >
                  <FolderOpen className="w-4 h-4" />
                  Z biblioteki
                </Button>
                <label className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload}
                    disabled={uploadingImage}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5"
                    asChild
                  >
                    <span>
                      {uploadingImage ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      Wgraj
                    </span>
                  </Button>
                </label>
              </div>
              
              {/* Dialog wyboru z biblioteki */}
              {showImagePicker && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                  <Card className="w-full max-w-4xl max-h-[80vh] flex flex-col">
                    <CardHeader className="py-3 border-b flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FolderOpen className="w-5 h-5" />
                          Biblioteka mediow
                        </CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => setShowImagePicker(false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4 flex-1 overflow-hidden flex flex-col">
                      {/* Kategorie */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        <Button
                          variant={selectedMediaCategory === "" ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleCategoryChange("")}
                        >
                          Wszystkie
                        </Button>
                        {mediaCategories.map((cat) => (
                          <Button
                            key={cat}
                            variant={selectedMediaCategory === cat ? "default" : "outline"}
                            size="sm"
                            onClick={() => handleCategoryChange(cat)}
                          >
                            {cat}
                          </Button>
                        ))}
                      </div>
                      
                      {/* Siatka obrazków */}
                      <div className="flex-1 overflow-y-auto">
                        {loadingMedia ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                          </div>
                        ) : mediaImages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                            <Image className="w-12 h-12 mb-3 opacity-30" />
                            <p className="text-sm">Brak obrazkow w tej kategorii</p>
                            <p className="text-xs mt-1">Wgraj obrazki lub wybierz inna kategorie</p>
                          </div>
                        ) : (
                          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                            {mediaImages.map((img) => (
                              <button
                                key={img.id}
                                onClick={() => selectMediaImage(img)}
                                className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all group bg-slate-100"
                              >
                                <img
                                  src={img.url?.startsWith("http") ? img.url : `https://epidemos.pl${img.url}`}
                                  alt={img.alt || img.filename}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                  <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                                    Wybierz
                                  </span>
                                </div>
                                {img.category && (
                                  <Badge className="absolute bottom-1 left-1 text-[9px] py-0 h-4">
                                    {img.category}
                                  </Badge>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Upload w dialogu */}
                      <div className="mt-4 pt-4 border-t flex-shrink-0">
                        <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={handleFileUpload}
                            disabled={uploadingImage}
                          />
                          {uploadingImage ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                              <span className="text-sm text-blue-600">Wgrywanie...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-slate-400" />
                              <span className="text-sm text-slate-600">Kliknij aby wgrac nowy obrazek</span>
                            </>
                          )}
                        </label>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SEO */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm">SEO</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Meta tytul ({metaTitle.length}/60)</label>
                <Input
                  placeholder="Tytul SEO..."
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value.substring(0, 60))}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Meta opis ({metaDescription.length}/155)</label>
                <Textarea
                  placeholder="Opis SEO..."
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value.substring(0, 155))}
                  className="text-sm min-h-[60px]"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Tagi (przecinek)
                </label>
                <Input
                  placeholder="seo, marketing, content..."
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Krotki opis (excerpt)</label>
                <Textarea
                  placeholder="Krotki opis do podgladu..."
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value.substring(0, 300))}
                  className="text-sm min-h-[60px]"
                />
              </div>
            </CardContent>
          </Card>

          {/* Publikacja */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Publikacja
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Status</label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Szkic</SelectItem>
                    <SelectItem value="published">Opublikowany</SelectItem>
                    <SelectItem value="scheduled">Zaplanowany</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {status === "scheduled" && (
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Data i godzina publikacji</label>
                  <Input
                    type="datetime-local"
                    value={scheduledAt ? scheduledAt.substring(0, 16) : ""}
                    onChange={(e) => setScheduledAt(e.target.value ? new Date(e.target.value).toISOString() : "")}
                    className="text-sm"
                  />
                </div>
              )}

              <div className="pt-2 border-t">
                <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                  <RefreshCw className="w-3 h-3" /> Auto-generowanie cykliczne
                </label>
                <Select 
                  value={autoScheduleDays?.toString() || "none"} 
                  onValueChange={(v) => setAutoScheduleDays(v === "none" ? null : parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Wylaczone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Wylaczone</SelectItem>
                    <SelectItem value="1">Codziennie</SelectItem>
                    <SelectItem value="2">Co 2 dni</SelectItem>
                    <SelectItem value="3">Co 3 dni</SelectItem>
                    <SelectItem value="4">Co 4 dni</SelectItem>
                    <SelectItem value="5">Co 5 dni</SelectItem>
                    <SelectItem value="6">Co 6 dni</SelectItem>
                    <SelectItem value="7">Co tydzien</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {autoScheduleDays && (
                <div>
                  <label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Godzina publikacji
                  </label>
                  <Select value={autoScheduleHour.toString()} onValueChange={(v) => setAutoScheduleHour(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map(h => (
                        <SelectItem key={h} value={h.toString()}>{h}:00</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
